/**
 * Componente de Login con Azure AD para Administradores
 * Utiliza Microsoft Azure AD para autenticación corporativa
 */

import React, { useState } from 'react';
import styled from 'styled-components';
import { LogIn, ArrowLeft, Users, AlertCircle } from 'lucide-react';
import { generateAuthUrl } from '../../utils/azureAuth';
import { useAzureConfig } from '../../hooks/useAzureConfig';

const LoginContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  padding: ${({ theme }) => theme.spacing.lg};
  position: relative;
`;

const BackButton = styled.button`
  position: absolute;
  top: ${({ theme }) => theme.spacing.lg};
  left: ${({ theme }) => theme.spacing.lg};
  background: rgba(255, 255, 255, 0.2);
  border: 2px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateX(-2px);
  }
`;

const LoginCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  box-shadow: ${({ theme }) => theme.shadows.xl};
  padding: ${({ theme }) => theme.spacing.xl};
  width: 100%;
  max-width: 400px;
`;

const Logo = styled.div`
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const UserTypeBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  background: #10b98120;
  color: #10b981;
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const LogoIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${({ theme }) => theme.spacing.md};
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.textPrimary};
  text-align: center;
  margin: 0 0 ${({ theme }) => theme.spacing.xs} 0;
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: center;
  margin: 0 0 ${({ theme }) => theme.spacing.xl} 0;
`;

const Button = styled.button`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.md};
  background: #0078d4;
  color: white;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.xs};
  margin-top: ${({ theme }) => theme.spacing.md};

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
    background: #106ebe;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const InfoSection = styled.div`
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid #e0e0e0;
  text-align: center;
`;

const InfoItem = styled.p`
  font-size: 0.9rem;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`;

const ErrorMessage = styled.div`
  background: ${({ theme }) => theme.colors.error}10;
  border: 1px solid ${({ theme }) => theme.colors.error}30;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const LoadingSpinner = styled.div`
  border: 3px solid #f3f3f3;
  border-top: 3px solid #0078d4;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

interface AzureLoginProps {
  onBack: () => void;
}

const AzureLogin: React.FC<AzureLoginProps> = ({ onBack }) => {
  const [isLogging, setIsLogging] = useState(false);
  const { config, loading: configLoading, error: configError } = useAzureConfig();

  const handleAzureLogin = async () => {
    if (!config || !config.webConfig) {
      console.error('Web config no está disponible');
      return;
    }

    console.log('Iniciando login con Azure AD...');
    console.log('Web config:', config.webConfig);
    
    setIsLogging(true);
    try {
      // Generar URL de autorización y redirigir a Azure AD
      const authUrl = generateAuthUrl(config.webConfig);
      console.log('Auth URL generada:', authUrl);
      console.log('Redirigiendo a Azure AD...');
      
      // Redirigir a Azure AD
      window.location.href = authUrl;
    } catch (error) {
      console.error('Error durante el login:', error);
      setIsLogging(false);
    }
  };

  // Mostrar pantalla de carga mientras se obtiene la configuración
  if (configLoading) {
    return (
      <LoginContainer>
        <LoginCard>
          <Logo>
            <LoadingSpinner style={{ margin: '0 auto 20px' }} />
            <Title>Cargando...</Title>
            <Subtitle>
              Obteniendo configuración de autenticación...
            </Subtitle>
          </Logo>
        </LoginCard>
      </LoginContainer>
    );
  }

  // Mostrar error si no se pudo obtener la configuración
  if (configError) {
    return (
      <LoginContainer>
        <BackButton onClick={onBack}>
          <ArrowLeft size={20} />
          Volver
        </BackButton>
        
        <LoginCard>
          <Logo>
            <Title>❌ Error de Configuración</Title>
            <ErrorMessage>
              <AlertCircle size={16} />
              No se pudo cargar la configuración de autenticación.
              {configError.message && <br />}
              {configError.message}
            </ErrorMessage>
          </Logo>
        </LoginCard>
      </LoginContainer>
    );
  }

  return (
    <LoginContainer>
      <BackButton onClick={onBack}>
        <ArrowLeft size={20} />
        Volver
      </BackButton>
      
      <LoginCard>
        <Logo>
          <UserTypeBadge>
            <Users size={16} />
            Administrador
          </UserTypeBadge>
          <LogoIcon>
            <LogIn size={40} color="white" />
          </LogoIcon>
          <Title>Bienvenido</Title>
          <Subtitle>Inicia sesión como administrador</Subtitle>
        </Logo>

        <div>
          <Button
            onClick={handleAzureLogin}
            disabled={isLogging || !config}
          >
            {isLogging ? (
              <>
                <LoadingSpinner />
                Iniciando sesión...
              </>
            ) : (
              <>
                <LogIn size={18} />
                Iniciar Sesión con Microsoft
              </>
            )}
          </Button>

          <InfoSection>
            <InfoItem>
              <span>🔒</span>
              <span>Autenticación segura con Azure AD</span>
            </InfoItem>
            <InfoItem>
              <span>🏢</span>
              <span>Acceso para personal interno</span>
            </InfoItem>
          </InfoSection>
        </div>
      </LoginCard>
    </LoginContainer>
  );
};

export default AzureLogin;

